// API endpoints - Try proxy first, fallback to direct
const PROXY_BASE_URL = "/api/work";
const PROXY_PRESS_URL = "/api/presses";
const DIRECT_BASE_URL = "https://admin.nathanportelli.com/wp-json/wp/v2/work";
const DIRECT_PRESS_URL =
  "https://admin.nathanportelli.com/wp-json/wp/v2/presses";

// Use proxy endpoints since proxy is working
export const API_BASE_URL = PROXY_BASE_URL;
export const PRESS_API_URL = PROXY_PRESS_URL;

// Test function to check if proxy is working
export async function testProxy() {
  try {
    const response = await fetch("/test");
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Proxy test failed:", error);
    return null;
  }
}

// Data fetching function with fallback
export async function fetchWorks() {
  // Try proxy first
  try {
    const response = await fetch(API_BASE_URL, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`Proxy HTTP error! status: ${response.status}`);
    }

    // Check if response is actually JSON
    const contentType = response.headers.get("content-type");

    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }

    const data = await response.json();
    return data;
  } catch (proxyError) {
    console.warn("Proxy failed, trying direct API:", proxyError);

    // Fallback to direct API
    try {
      const response = await fetch(DIRECT_BASE_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(`Direct HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (directError) {
      console.error("Both proxy and direct API failed:", directError);
      return [];
    }
  }
}

// Press data fetching function with fallback
export async function fetchPress() {
  // Try proxy first
  try {
    const response = await fetch(PRESS_API_URL, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`Proxy HTTP error! status: ${response.status}`);
    }

    // Check if response is actually JSON
    const contentType = response.headers.get("content-type");

    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }

    const data = await response.json();
    return data;
  } catch (proxyError) {
    console.warn("Proxy failed, trying direct API:", proxyError);

    // Fallback to direct API
    try {
      const response = await fetch(DIRECT_PRESS_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(`Direct HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (directError) {
      console.error("Both proxy and direct API failed:", directError);
      return [];
    }
  }
}

// Helper function to extract year from date string
function extractYearFromDate(dateString) {
  if (!dateString || dateString === "Unknown") return "Unknown";

  // Try to extract year from various date formats
  const yearMatch = dateString.match(/(\d{4})/);
  return yearMatch ? yearMatch[1] : "Unknown";
}

// Helper function to get sortable date for sorting
function getSortableDate(dateString) {
  if (!dateString || dateString === "Unknown") return new Date(0); // Very old date for sorting

  // Try to parse the date string
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? new Date(0) : date;
}

// Transform API data to our format
export function transformWorkData(apiWork) {
  const workDate = apiWork.work_date || "Unknown";

  return {
    id: apiWork.slug || apiWork.id.toString(),
    title: apiWork.work_title || apiWork.title.rendered || "Untitled",
    year: extractYearFromDate(workDate),
    sortableDate: getSortableDate(workDate),
    location: apiWork.work_location || "Unknown",
    curator: apiWork.curated_by || "Unknown",
    headerImage: apiWork.work_header_image || "",
    description: apiWork.work_description || "",
    works: (() => {
      // Handle different work_piece data structures
      if (!apiWork.work_piece) return [];

      // If it's an array, process normally
      if (Array.isArray(apiWork.work_piece)) {
        return apiWork.work_piece.map((piece, index) => ({
          images: piece.piece_images
            ? piece.piece_images.map((img) => img.piece_image)
            : [],
          description: piece.piece_description || "",
          column_layout: piece.column_layout || false,
        }));
      }

      // If it's an object with numeric keys, convert to array
      if (typeof apiWork.work_piece === "object") {
        return Object.values(apiWork.work_piece).map((piece, index) => ({
          images: piece.piece_images
            ? piece.piece_images.map((img) => img.piece_image)
            : [],
          description: piece.piece_description || "",
          column_layout: piece.column_layout || false,
        }));
      }

      return [];
    })(),
  };
}

// Transform press API data to our format
export function transformPressData(apiPress) {
  const pressDate = apiPress.press_date || "Unknown";

  return {
    id: apiPress.id,
    title: apiPress.press_title || apiPress.title.rendered || "Untitled",
    publisher: apiPress.press_publisher || "Unknown",
    date: extractYearFromDate(pressDate),
    sortableDate: getSortableDate(pressDate),
    link: apiPress.press_link || apiPress.link || "",
  };
}
